/**
 * Провод между клиентом и сервером.
 *
 * Здесь лежит контракт: что клиент шлёт, что сервер присылает и как это
 * упаковано. Правил игры тут нет — они в game-state.ts, канон механики и
 * разбор контракта лежат в README.md.
 *
 * Это единственный модуль, из которого разрешено импортировать клиенту.
 * Всё, что ему нужно, реэкспортируется внизу файла; в game-state.ts он не
 * заглядывает. Границу сторожит проверка в tests/state.test.ts.
 *
 * Два канала Socket.IO и ровно два имени событий:
 *   клиент -> сервер: 'action'  с телом ClientAction
 *   сервер -> клиент: 'event'   с телом ServerMessage
 *
 * Идентичность игрока в теле команды НЕ передаётся: сервер берёт её из своей
 * сессии (socket.id). Клиент физически не может прислать чужой playerId,
 * потому что такого поля в протоколе нет.
 */

import { AVATAR_PATTERN, NAME_MAX, PAWNS, ROOM_NAME_MAX, cleanText } from './game-state.js'
import type { Command, ServerEvent, PawnId, RoomSummary } from './game-state.js'

/**
 * Комната в списке лобби: сводка ядра плюс число зрителей. Зрители живут только
 * в транспорте, поэтому добавляет их сервер, а не ядро.
 */
export type RoomInfo = RoomSummary & { viewers: number }

/** Предел длины имени клиента на стенде. */
const HARNESS_NAME_MAX = 20

/** Имя события Socket.IO для команд клиента. */
export const CHANNEL_ACTION = 'action' as const
/** Имя события Socket.IO для сообщений сервера. */
export const CHANNEL_EVENT = 'event' as const

// ============================================================================
// ИГРОВЫЕ ДЕЙСТВИЯ: клиент -> сервер
// ============================================================================

/**
 * Создание партии. Единственное действие, не адресованное существующей игре,
 * поэтому кода партии в нём нет — сервер его выдаёт.
 */
export interface CreateGameAction {
  kind: 'createGame'
  commandId: string
  name: string
  /** Название комнаты, которое ввёл создатель. Пусто — сервер возьмёт код. */
  roomName?: string
  pawn?: PawnId
  /** Выбранная аватарка, см. `Player.avatar`. */
  avatar?: string
}

/**
 * Вход в существующую партию по коду либо возврат в свой слот по токену.
 * Токен важнее кода: он работает в любой фазе, включая идущую партию.
 */
export interface JoinGameAction {
  kind: 'joinGame'
  commandId: string
  code: string
  name: string
  pawn?: PawnId
  /** Выбранная аватарка, см. `Player.avatar`. */
  avatar?: string
  sessionToken?: string
}

/** Действия внутри партии. Один в один команды ядра, см. Command. */
export interface GameCommandAction {
  kind: 'command'
  command: Command
}

export type GameAction = CreateGameAction | JoinGameAction | GameCommandAction | WatchGameAction

// ============================================================================
// СЛУЖЕБНЫЕ ДЕЙСТВИЯ СТЕНДА: клиент -> сервер
// ============================================================================

/** Представиться стенду. Имя нужно только для списка подключённых. */
export interface HarnessHelloAction {
  kind: 'harness:hello'
  name: string
}

/** Замер задержки. t0 возвращается нетронутым, длительность считает клиент. */
export interface HarnessPingAction {
  kind: 'harness:ping'
  t0: number
}

/**
 * Смотреть партию, не участвуя. code — за какой комнатой следить, null —
 * перестать. Зритель не занимает слот и получает только публичный снимок.
 */
export interface WatchGameAction {
  kind: 'watchGame'
  code: string | null
}

/** Удалить комнату из общего списка и завершить её партию. */
export interface DeleteRoomAction {
  kind: 'deleteRoom'
  code: string
}

/** Разорвать соединение указанного клиента: так проверяются обрыв и восстановление. */
export interface HarnessEjectAction {
  kind: 'harness:eject'
  id: string
}

/**
 * Подсказка кубику: следующий бросок комнаты даст `value`, как бы ни держали кубик. Только для сквозных
 * проверок: сервер принимает её лишь в тестовом режиме (`DURATION_ENV`), в игре действие отклоняется.
 */
export interface HarnessDiceAction {
  kind: 'harness:dice'
  code: string
  value: number
}

/**
 * Сократить срок текущей фазы комнаты до `ms` мс от сейчас. Только для сквозных проверок (тестовый режим сервера):
 * срок по-прежнему кончает сервер своими часами, проверка лишь не ждёт настоящих секунд.
 */
export interface HarnessDeadlineAction {
  kind: 'harness:deadline'
  code: string
  ms: number
}

/** Остановить часы комнаты (`on`) или пустить дальше. Только для сквозных проверок (тестовый режим сервера). */
export interface HarnessHoldAction {
  kind: 'harness:hold'
  code: string
  on: boolean
}

export type HarnessAction =
  | HarnessHelloAction
  | HarnessPingAction
  | HarnessEjectAction
  | HarnessDiceAction
  | HarnessDeadlineAction
  | HarnessHoldAction
  | DeleteRoomAction

export type ClientAction = GameAction | HarnessAction

// ============================================================================
// СООБЩЕНИЯ СЕРВЕРА: сервер -> клиент
// ============================================================================

/** Любое событие игрового ядра. Список — в ServerEvent. */
export interface GameEventMessage {
  kind: 'game'
  event: ServerEvent
}

/** Комнату закрыли: она удалена, и играть или смотреть в ней больше нечего. */
export interface RoomClosedMessage {
  kind: 'roomClosed'
  reason: string
}

/**
 * Дерево комнат лобби, одно на всех. Одно событие несёт и полный список, и
 * фрагмент: клиент держит дерево и правит его, а не пересобирает.
 *
 * - `full: true` — полный список: клиент заменяет им всё. Приходит при
 *   подключении.
 * - `full: false` — фрагмент: `upsert` добавляет или заменяет комнаты по коду,
 *   `remove` убирает. Приходит только если что-то изменилось, и только про то,
 *   что изменилось.
 *
 * Порядок комнат — порядок создания: новая встаёт в конец, обновлённая остаётся
 * на месте. Потерь фрагментов нет: поток упорядочен, а после обрыва клиент
 * получает полный список заново.
 */
export interface RoomsMessage {
  kind: 'rooms'
  full: boolean
  upsert: RoomInfo[]
  remove: string[]
}

/**
 * Правка дерева комнат по событию `rooms`. Полный список заменяет дерево, фрагмент
 * правит его: сначала убирает, потом добавляет или заменяет по коду. Порядок —
 * как на сервере: новая комната в конец, обновлённая остаётся на месте.
 */
export function applyRooms(current: readonly RoomInfo[] | null, message: RoomsMessage): RoomInfo[] {
  if (message.full) return message.upsert
  const gone = new Set(message.remove)
  const fresh = new Map(message.upsert.map((r) => [r.code, r]))
  const kept = (current ?? []).filter((r) => !gone.has(r.code)).map((r) => fresh.get(r.code) ?? r)
  const known = new Set(kept.map((r) => r.code))
  return [...kept, ...message.upsert.filter((r) => !known.has(r.code))]
}

/** Полное состояние стенда. Приходит при входе и при каждом изменении списка. */
export interface HarnessStateMessage {
  kind: 'harness:state'
  state: HarnessState
}

/** Ответ на ping. t0 возвращается как пришёл. */
export interface HarnessPongMessage {
  kind: 'harness:pong'
  t0: number
  serverIso: string
}

export type ServerMessage =
  GameEventMessage | RoomClosedMessage | RoomsMessage | HarnessStateMessage | HarnessPongMessage

// ============================================================================
// КОНТРОЛЬНЫЕ ТОЧКИ ЗАЩИТЫ ОТ XSS
// ============================================================================
//
// Любая строка, которую вводит человек, доезжает до чужого экрана. Защита не
// размазана по полям: у неё ровно две двери, по одной на каждую сторону.
//
//   сервер: `checkAction`  — всё, что пришло от клиента, до ядра
//   клиент: `scrubMessage` — всё, что пришло от сервера, до отрисовки
//
// Правило одно: в дверь входит неизвестно что, из неё выходит только
// проверенная форма с белым списком полей. Строки очищаются от управляющих и
// невидимых знаков (`cleanText`), идентификаторы сужаются до `[A-Za-z0-9_-]`,
// цвет — до известных. Разметка при этом не вырезается, а значит и не
// «чинится»: её просто нигде не интерпретируют — клиент выводит текст через
// React, а `dangerouslySetInnerHTML` кормится только собственными SVG из
// сборки. Новый динамический путь в HTML — повод идти через эти двери, а не
// рядом с ними.

const SAFE_ID = /^[A-Za-z0-9_-]{1,200}$/
const PAWN_IDS: readonly string[] = PAWNS.map((p) => p.id)

/** Правило поля: очищенное значение, `undefined` (поля нет) или `BAD`. */
const BAD = Symbol('bad')
type Rule = (v: unknown) => unknown
const str = (v: unknown, ok: (s: string) => boolean): v is string => typeof v === 'string' && ok(v)
const id: Rule = (v) => (str(v, (s) => SAFE_ID.test(s)) ? v : BAD)
const num: Rule = (v) => (typeof v === 'number' && Math.abs(v) <= 1e15 ? v : BAD)
const text =
  (max: number): Rule =>
  (v) =>
    str(v, (s) => s.length <= max * 4) ? cleanText(v).slice(0, max) : BAD
const code: Rule = (v) => (str(v, (s) => s.length <= 40) ? cleanText(v) : BAD)
const opt =
  (rule: Rule): Rule =>
  (v) =>
    v == null ? undefined : rule(v)
const avatar = opt((v) => (v === '' || str(v, (s) => AVATAR_PATTERN.test(s)) ? v : BAD))
const pawn = opt((v) => (str(v, (s) => PAWN_IDS.includes(s)) ? v : BAD))

/** Что принимает сервер: поля каждого действия и каждой команды. Чего нет в таблице, того нет. */
const ACTIONS: Record<string, Record<string, Rule>> = {
  'harness:hello': { name: text(HARNESS_NAME_MAX) },
  'harness:ping': { t0: num },
  'harness:eject': { id },
  'harness:dice': { code, value: num },
  'harness:deadline': { code, ms: num },
  'harness:hold': { code, on: (v) => (typeof v === 'boolean' ? v : BAD) },
  deleteRoom: { code },
  createGame: { commandId: id, name: text(NAME_MAX), roomName: opt(text(ROOM_NAME_MAX)), pawn, avatar },
  joinGame: { commandId: id, code, name: text(NAME_MAX), pawn, avatar, sessionToken: opt(id) },
  watchGame: { code: (v) => (v === null ? null : code(v)) },
}
// `joinGame` командой не принимается: вход идёт отдельным действием.
const COMMANDS: Record<string, Record<string, Rule>> = {
  startGame: {},
  leaveGame: {},
  setAutoStart: { min: num },
  acknowledge: { turn: num },
  closeResult: { turn: num },
  rollDice: { turn: num, holdMs: num },
  submitAnswer: { turn: num, questionId: id, answerId: id },
}

/** Результат проверки входящего действия: очищенная копия либо причина отказа. */
export type CheckedAction = { ok: true; action: ClientAction } | { ok: false; reason: string }

const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/** Новый объект только из полей таблицы: лишние ключи (в том числе `__proto__`) не проходят. `null` — поле не прошло. */
function pick(raw: Record<string, unknown>, spec: Record<string, Rule>): Record<string, unknown> | null {
  const out: Record<string, unknown> = {}
  for (const [key, rule] of Object.entries(spec)) {
    const value = rule(raw[key])
    if (value === BAD) return null
    if (value !== undefined) out[key] = value
  }
  return out
}

/**
 * Серверная дверь. Имя и название комнаты очищаются, а не отклоняются:
 * «Ｃｌａｕｄｅ\u200b» станет «Claude», и уникальность проверит уже настоящее имя.
 */
export function checkAction(raw: unknown): CheckedAction {
  const kind = record(raw) ? raw.kind : undefined
  if (!record(raw) || typeof kind !== 'string') return { ok: false, reason: 'Действие без поля kind' }
  let action: Record<string, unknown> | null = null
  if (kind === 'command') {
    const c = raw.command
    const type = record(c) ? c.type : undefined
    if (record(c) && typeof type === 'string' && Object.hasOwn(COMMANDS, type)) {
      const command = pick(c, { commandId: id, ...COMMANDS[type] })
      action = command && { command: { type, ...command } }
    }
  } else if (Object.hasOwn(ACTIONS, kind)) action = pick(raw, ACTIONS[kind])
  return action
    ? { ok: true, action: { kind, ...action } as ClientAction }
    : { ok: false, reason: 'Неверное действие' }
}

const safeName = (v: unknown, max = NAME_MAX): string => cleanText(v).slice(0, max)
const safeAvatar = (v: unknown): string => (typeof v === 'string' && AVATAR_PATTERN.test(v) ? v : '')
const safePawn = (v: unknown): PawnId =>
  PAWN_IDS.includes(v as string) ? (v as PawnId) : (PAWNS[0].id as PawnId)
const safeCode = (v: unknown): string => (typeof v === 'string' && SAFE_ID.test(v) ? v : '')

/**
 * Клиентская дверь: чистит сообщение сервера на месте, до того как оно попадёт
 * в состояние и на экран. Сервер свои данные уже очистил (`checkAction`), эта
 * проверка — второй рубеж на случай чужого или скомпрометированного сервера и
 * старых данных. Трогает только поля, написанные людьми.
 */
export function scrubMessage(message: ServerMessage): ServerMessage {
  const players = (list: unknown): void => {
    if (!Array.isArray(list)) return
    for (const p of list) {
      p.name = safeName(p.name)
      p.avatar = safeAvatar(p.avatar)
      p.pawn = safePawn(p.pawn)
    }
  }
  const snapshot = (
    sn:
      | {
          code: string
          roomName: string
          players: unknown
          pendingMove?: { from: string; path: unknown; dice: number | null } | null
        }
      | undefined,
  ): void => {
    if (!sn) return
    sn.code = safeCode(sn.code)
    sn.roomName = safeName(sn.roomName, ROOM_NAME_MAX)
    players(sn.players)
    if (sn.pendingMove) {
      sn.pendingMove.from = safeCode(sn.pendingMove.from)
      sn.pendingMove.path = Array.isArray(sn.pendingMove.path) ? sn.pendingMove.path.map(safeCode) : []
    }
  }
  switch (message.kind) {
    case 'rooms':
      for (const r of message.upsert) {
        r.code = safeCode(r.code)
        r.roomName = safeName(r.roomName, ROOM_NAME_MAX)
        for (const seat of r.seats) {
          seat.name = safeName(seat.name)
          seat.avatar = safeAvatar(seat.avatar)
          seat.pawn = safePawn(seat.pawn)
        }
      }
      message.remove = message.remove.map(safeCode)
      break
    case 'game':
      if (message.event.type === 'joined' || message.event.type === 'snapshot')
        snapshot(message.event.snapshot)
      break
    case 'harness:state':
      for (const c of message.state.clients) {
        c.name = safeName(c.name, HARNESS_NAME_MAX)
        if (c.playerId !== undefined) c.playerId = safeCode(c.playerId)
      }
      break
    default:
      break
  }
  return message
}

// ============================================================================
// СОСТОЯНИЕ СТЕНДА
// ============================================================================

export interface HarnessClient {
  id: string
  name: string
  connectedAtIso: string
  /** Игровой слот этой вкладки, если соединение вошло в партию игроком. */
  playerId?: string
}

/**
 * Состояние стенда. revision растёт на единицу при настоящем изменении списка:
 * attach, rename, привязка к игроку, detach. Это версия для показа, а не часы — разрывы в
 * нумерации никто не ищет, потерю сообщений ловит транспорт.
 */
export interface HarnessState {
  revision: number
  serverStartedIso: string
  clients: HarnessClient[]
}

// ============================================================================
// РЕЭКСПОРТ ДЛЯ КЛИЕНТА
// ============================================================================
//
// Клиент импортирует только отсюда. Значения — перечисления, которые он
// сверяет с состоянием, и несколько констант и чистых функций; всё остальное типы, они исчезают при сборке.
// Логика игры сюда не попадает: движок остаётся на сервере.

export {
  Phase,
  CellType,
  TrapStatus,
  TurnEvent,
  RejectCode,
  NAME_MAX,
  ROOM_NAME_MAX,
  MAX_ROOMS,
  PAWNS,
  positionOf,
} from './game-state.js'

/*
 * Сборка поля — чистые функции, движка в них нет: план из seed и разворот
 * плана в граф. Нужны стенду, который рисует случайные поля без партии и без
 * сокета. Все правила раскладки живут внутри них, поэтому стенд не может
 * нарисовать поле, которого не бывает в игре.
 */
export { generateBoardPlan, createBoard, rngFrom, DEFAULT_BOARD_RULES } from './game-state.js'

export type {
  Board,
  BoardRules,
  BoardNode,
  BoardEdge,
  BoardPlan,
  SwampPlan,
  Command,
  CommandResult,
  Player,
  PawnId,
  PublicQuestion,
  RoomSeat,
  Snapshot,
  TurnRecord,
} from './game-state.js'
